import { useCallback, useEffect, useMemo } from 'react'
import { deleteCookies } from './deleteCookies'
import { addTrackingEvents } from './addTrackingEvents'
import { useEventTracker } from '@hooks/useEventTracker'
import { CookieNoteContext } from '@contexts/CookieNoteContext'
import { logger } from '@core/logger'
import { useGetCountryLanguage } from '@hooks/useGetCountryLanguage'

type OneTrustCookieNoteProps = {
  children: React.ReactNode
  askNotToTrack: boolean
}

export const OneTrustCookieNote = ({
  children,
  askNotToTrack,
}: OneTrustCookieNoteProps) => {
  const allowCookieNote = !askNotToTrack
  const { language, country } = useGetCountryLanguage()
  const { track } = useEventTracker()

  useEffect(() => {
    // delete old GDPR cookies for all users as we don't mention these cookies in OneTrust banner
    // they should not be set anymore for any user
    document.cookie =
      'num26GDPR=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
    document.cookie =
      'num26CookiesGDPR=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
  }, [])

  const trackingParams = useMemo(
    () => ({
      locale: language,
      country: country.toUpperCase(),
    }),
    [language, country]
  )

  const openCookieNote = useCallback(() => {
    if (typeof window.OneTrust === 'undefined') {
      return logger.error(
        'ONE_TRUST_LOADING_ERROR',
        "typeof window.OneTrust === 'undefined'"
      )
    }

    window.OneTrust.ToggleInfoDisplay()

    track({ action: 'onetrust_cookie_banner.pc_open', label: trackingParams })
  }, [track, trackingParams])

  const manageCookieDeletion = useCallback(() => {
    // preserve previous state of OnetrustActiveGroups to be able to compare
    // previous and current state and figure out what cookies should be deleted
    if (typeof window.previousOnetrustActiveGroups === 'undefined') {
      window.previousOnetrustActiveGroups = window.OnetrustActiveGroups
    }

    window.OneTrust?.OnConsentChanged(deleteCookies)
  }, [])

  useEffect(() => {
    // this logic is for the case when OneTrust has been initialised
    if (!allowCookieNote || !window.OneTrust) return

    // inject or remove GTM script based on user's consent to functional cookies
    // and add OnConsentChanged callback to inject or remove GTM script dynamically
    manageCookieDeletion()

    const cleanUpTrackingEvents = addTrackingEvents(track, trackingParams)

    return cleanUpTrackingEvents
  }, [allowCookieNote, manageCookieDeletion, track, trackingParams])

  useEffect(() => {
    // this logic is for the case when OneTrust has NOT been initialised
    if (!allowCookieNote || window.OneTrust || !window.MutationObserver) return

    const onetrustRootElementId = 'onetrust-consent-sdk'
    let cleanUpTrackingEvents: () => void

    // wait until the banner html element appears in the DOM
    // and manage GTM script and add snowplow tracking events after that
    const observer = new window.MutationObserver((mutationList, observer) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          for (const node of Array.from(mutation.addedNodes) as Element[]) {
            if (node.ELEMENT_NODE && node.id === onetrustRootElementId) {
              cleanUpTrackingEvents = addTrackingEvents(track, trackingParams)
              manageCookieDeletion()

              observer.disconnect()

              return
            }
          }
        }
      }
    })

    observer.observe(document.body, {
      childList: true,
    })

    return () => {
      observer.disconnect()
      cleanUpTrackingEvents?.()
    }
  }, [allowCookieNote, manageCookieDeletion, track, trackingParams])

  return (
    <CookieNoteContext.Provider
      value={{
        openCookieNote,
        allowCookieNote,
      }}
    >
      {children}
    </CookieNoteContext.Provider>
  )
}
